/**
 * @fileoverview TypeScript function to consume the JSON output from the
 * Google Apps Script and render it into an HTML string.
 */

// Define interfaces for the JSON structure to ensure type safety.

/**
 * Represents a segment of text within a paragraph or list item,
 * including its bold status.
 */
interface RichTextElement {
  text: string;
  bold: boolean;
}

/**
 * Represents a paragraph element from the Google Doc.
 */
interface ParagraphElement {
  type: "paragraph";
  elements: RichTextElement[];
  heading: string; // e.g., 'NORMAL', 'HEADING1', 'HEADING2'
}

/**
 * Represents a list item element from the Google Doc.
 */
interface ListItemElement {
  type: "listItem";
  elements: RichTextElement[];
  glyph: string; // e.g., 'BULLET', 'NUMBER'
  nestingLevel: number;
}

/**
 * Represents a table element from the Google Doc.
 */
interface TableElement {
  type: "table";
  data: string[][]; // Array of rows, where each row is an array of cell strings.
}

/**
 * Represents a section of the Google Doc, typically starting with a heading.
 */
interface DocumentSection {
  type: "section";
  title: string;
  headingLevel: string; // e.g., 'HEADING1', 'HEADING2', 'NORMAL' for introduction
  content: (ParagraphElement | ListItemElement | TableElement)[];
  lastUpdated?: string; // ISO date string like "2025-06-06T10:24:05.479Z"
}

/**
 * The main interface for the entire parsed Google Doc content.
 */
export type GoogleDocContent = DocumentSection[];

/**
 * Options for controlling how the Google Doc content is rendered.
 */
export interface RenderOptions {
  /** Whether to show document titles (HEADING1) */
  showDocumentTitle?: boolean;
  /** Whether to show section titles (HEADING2 and others) */
  showSectionTitles?: boolean;
  /** Whether to hide the first section title specifically */
  hideFirstSectionTitle?: boolean;
  /** Whether to hide the first paragraph title in each section */
  hideFirstParagraphTitle?: boolean;
  /** Whether to use custom CSS classes */
  useCustomClass?: boolean;
  /** Custom wrapper class */
  wrapperClass?: string;
}

/**
 * Default rendering options
 */
const DEFAULT_OPTIONS: RenderOptions = {
  showDocumentTitle: true,
  showSectionTitles: true,
  hideFirstSectionTitle: false,
  hideFirstParagraphTitle: false,
  useCustomClass: true,
  wrapperClass: "",
};

/**
 * Renders the rich text elements into an HTML string.
 * @param elements An array of RichTextElement objects.
 * @returns An HTML string with bold text appropriately tagged.
 */
function renderRichText(elements: RichTextElement[]): string {
  return elements
    .map((part) => {
      if (part.bold) {
        return `<strong>${part.text}</strong>`;
      }
      return part.text;
    })
    .join("");
}

/**
 * Consumes the Google Doc JSON and renders it into a structured HTML string.
 * This function can be used within a web component or a framework like React/Angular/Vue
 * to display the content.
 * @param jsonData The JSON object representing the Google Doc content.
 * @param options Optional rendering configuration
 * @returns A string containing the HTML representation of the document.
 */
export function renderGoogleDocContent(
  jsonData: GoogleDocContent,
  options?: RenderOptions,
): string {
  // Merge provided options with defaults
  const renderOptions = { ...DEFAULT_OPTIONS, ...options };
  let html = "";

  jsonData.forEach((section, index) => {
    // Determine if this section title should be shown
    const isFirstSection = index === 0;
    const isDocumentTitle = section.headingLevel === "HEADING1";
    const shouldShowTitle =
      (renderOptions.showDocumentTitle && isDocumentTitle) ||
      (renderOptions.showSectionTitles && !isDocumentTitle);

    // Special case for first section title
    if (isFirstSection && renderOptions.hideFirstSectionTitle) {
      // Skip title for first section if hideFirstSectionTitle is true
    } else if (section.title && shouldShowTitle) {
      // Render section title based on heading level
      if (section.headingLevel === "HEADING1") {
        const titleClass = renderOptions.useCustomClass
          ? 'class="heading-1"'
          : "";
        html += `<h1 ${titleClass}>${section.title}</h1>`;
      } else if (section.headingLevel === "HEADING2") {
        const titleClass = renderOptions.useCustomClass
          ? 'class="heading-2"'
          : "";
        html += `<h2 ${titleClass}>${section.title}</h2>`;
      } else {
        // For 'NORMAL' heading level, treat it as a general section title
        const titleClass = renderOptions.useCustomClass
          ? 'class="heading-general"'
          : "";
        html += `<h2 ${titleClass}>${section.title}</h2>`;
      }
    }

    // Section content wrapper
    const sectionClass = renderOptions.useCustomClass
      ? 'class="section-content"'
      : 'class="section-content"';
    html += `<div ${sectionClass}>`;

    section.content.forEach((element, elementIndex) => {
      if (element.type === "paragraph") {
        const paragraph = element as ParagraphElement;
        const renderedText = renderRichText(paragraph.elements);

        // Check if this is the first paragraph in the section
        const isFirstParagraph = elementIndex === 0;

        // Enhanced detection of paragraph headings - include paragraphs that:
        // 1. Are officially marked as headings (HEADING3, HEADING4)
        // 2. OR first paragraphs with all bold text
        // 3. OR first paragraphs with short content (likely a title)
        const isOfficialHeading =
          paragraph.heading === "HEADING3" || paragraph.heading === "HEADING4";
        const isAllBold =
          paragraph.elements.length > 0 &&
          paragraph.elements.every((el) => el.bold === true);
        const isShortContent = renderedText.length < 50; // Consider short paragraphs as potential titles

        const isParagraphHeading =
          isOfficialHeading ||
          (isFirstParagraph && (isAllBold || isShortContent));

        // Skip rendering this paragraph as a heading if it's the first paragraph and hideFirstParagraphTitle is true
        if (
          isFirstParagraph &&
          renderOptions.hideFirstParagraphTitle &&
          isParagraphHeading
        ) {
          // Option 1: Hide completely if it's clearly a title
          if (isAllBold && isShortContent) {
            // Skip rendering completely
            return;
          }

          // Option 2: Render as normal paragraph instead of as a heading
          const pClass = renderOptions.useCustomClass
            ? 'class="paragraph"'
            : "";
          html += `<p ${pClass}>${renderedText}</p>`;
        } else if (isOfficialHeading || isAllBold) {
          // Render as appropriate heading based on context
          if (paragraph.heading === "HEADING3") {
            const h3Class = renderOptions.useCustomClass
              ? 'class="heading-3"'
              : "";
            html += `<h3 ${h3Class}>${renderedText}</h3>`;
          } else if (paragraph.heading === "HEADING4") {
            const h4Class = renderOptions.useCustomClass
              ? 'class="heading-4"'
              : "";
            html += `<h4 ${h4Class}>${renderedText}</h4>`;
          } else if (isAllBold && isFirstParagraph) {
            // First paragraph with all bold text is treated as h3
            const h3Class = renderOptions.useCustomClass
              ? 'class="heading-3"'
              : "";
            html += `<h3 ${h3Class}>${renderedText}</h3>`;
          } else {
            // Other bold content gets standard paragraph treatment
            const pClass = renderOptions.useCustomClass
              ? 'class="paragraph-bold"'
              : "";
            html += `<p ${pClass}>${renderedText}</p>`;
          }
        } else {
          // Regular paragraph
          const pClass = renderOptions.useCustomClass
            ? 'class="paragraph"'
            : "";
          html += `<p ${pClass}>${renderedText}</p>`;
        }
      } else if (element.type === "listItem") {
        const listItem = element as ListItemElement;
        const renderedText = renderRichText(listItem.elements);
        // Use different list tags based on glyph type
        const indentStyle = `margin-left: ${listItem.nestingLevel * 20}px;`; // Indent based on nesting level

        // This approach assumes a flat list structure and will create new <ul>/<ol> for each item.
        // For proper nested lists, you'd need to group list items by nesting level.
        // For simplicity here, we'll just apply indentation.
        const liClass = renderOptions.useCustomClass ? 'class="list-item"' : "";
        html += `<li ${liClass} style="${indentStyle}">${renderedText}</li>`;
      } else if (element.type === "table") {
        const table = element as TableElement;

        const tableClass = renderOptions.useCustomClass
          ? 'class="table"'
          : 'class="table"';

        const tableWrapperClass = renderOptions.useCustomClass
          ? 'class="table-wrapper"'
          : 'class="table-wrapper"';

        html += `<div ${tableWrapperClass}><table ${tableClass}>`;

        table.data.forEach((row, rowIndex) => {
          html += `<tr>`;
          row.forEach((cell) => {
            if (rowIndex === 0) {
              // Assuming first row is header
              const thClass = renderOptions.useCustomClass
                ? 'class="table-header"'
                : 'class="table-header"';
              html += `<th ${thClass}>${cell}</th>`;
            } else {
              const tdClass = renderOptions.useCustomClass
                ? 'class="table-cell"'
                : 'class="table-cell"';
              html += `<td ${tdClass}>${cell}</td>`;
            }
          });
          html += `</tr>`;
        });
        html += `</table></div>`;
      }
    });
    html += `</div>`; // Close section-content div
  });

  if (renderOptions.useCustomClass) {
    const wrapperClass = renderOptions.wrapperClass || "";
    return `
      <div class="doc-container ${wrapperClass}">
        <div class="doc-content">
          ${html}
        </div>
      </div>
    `;
  } else {
    // Simpler wrapper when not using custom classes
    const wrapperClass = renderOptions.wrapperClass || "";
    return `<div class="doc-content ${wrapperClass}">${html}</div>`;
  }
}

/**
 * Extract the last updated date from a Google Doc JSON structure
 * @param jsonData The parsed Google Doc JSON data
 * @returns A formatted date string or undefined if not found
 */
export function extractLastUpdated(
  jsonData: GoogleDocContent,
): string | undefined {
  try {
    // First priority: Check for lastUpdated property directly in the JSON objects
    for (const section of jsonData) {
      // Look for lastUpdated as a direct property on any section or the root object
      const lastUpdatedValue = section.lastUpdated || section.lastUpdated;

      if (lastUpdatedValue && typeof lastUpdatedValue === "string") {
        try {
          // Try to parse as an ISO date string
          const date = new Date(lastUpdatedValue);
          if (!isNaN(date.getTime())) {
            // Format as Month Day, Year (e.g., "June 6, 2025")
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          }
          // If it's not a valid date but still a string, return it as is
          return lastUpdatedValue;
        } catch (dateError) {
          console.warn("Failed to parse lastUpdated date:", dateError);
          return lastUpdatedValue; // Return the raw value if parsing fails
        }
      }
    }

    // Second priority: Look for dates in section titles
    for (const section of jsonData) {
      if (section.title && /last\s+updated/i.test(section.title)) {
        const dateMatch = section.title.match(/(\w+\s+\d+,\s+\d{4})/i);
        if (dateMatch && dateMatch[1]) {
          return dateMatch[1];
        }
      }
    }

    // Third priority: Look for dates in paragraph content
    for (const section of jsonData) {
      for (const contentItem of section.content || []) {
        if (contentItem.type === "paragraph" && contentItem.elements) {
          const text = contentItem.elements.map((el) => el.text).join("");
          if (/last\s+updated/i.test(text)) {
            const dateMatch = text.match(/(\w+\s+\d+,\s+\d{4})/i);
            if (dateMatch && dateMatch[1]) {
              return dateMatch[1];
            }
          }
        }
      }
    }
  } catch (e) {
    console.error("Failed to extract last updated date:", e);
  }

  // Default fallback
  return undefined;
}
